import {
  NETWORK_ERROR_MODAL_TEXT,
  NETWORK_ERROR_MODAL_TITLE,
} from '@mm/constants/error-descriptions';
import { landmineNames } from '@mm/constants/landmine-names';
import AdAccountCRUDActions from '@mm/state/actions/AdAccountCRUDActions';
import AdConfigCRUDActions from '@mm/state/actions/AdConfigCRUDActions';
import ConnectedPageActions from '@mm/state/actions/ConnectedPageCRUDActions';
import MessengerAdCRUDActions from '@mm/state/actions/MessengerAdCRUDActions';
import sponsoredMessageCRUDActions from '@mm/state/actions/SponsoredMessageCRUDActions';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import AdConfigModel from '@mm/state/model/AdConfigModel';
import { MessengerAdModel } from '@mm/state/model/MessengerAdModel';
import { MessengerAdRemoteAdModel } from '@mm/state/model/MessengerAdRemoteAdModel';
import { SponsoredMessageModel } from '@mm/state/model/SponsoredMessageModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { isArray, keys } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { timeout } from '../../../../common/helpers/utils';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';

type FetcherResult<T> = Promise<{
  data: T[];
  next: string | null;
  error?: string;
}>;

export const isValidStep = <T, K extends keyof T>(
  fields: K[],
  validations: { [key in K]?: string[] },
) => {
  return (
    keys(validations).find(
      key => fields.findIndex((field: K) => field === key) !== -1,
    ) === undefined
  );
};

export const numericValue = (value?: string | number | null) => {
  return value || value === 0 ? value.toString() : '';
};

export const cleanupFetcher = (cursor?: string | null): FetcherResult<any> =>
  new Promise(resolve => {
    resolve({ next: null, data: [] });
  });

export const statefulPoller = <T extends {}>() => {
  const state: { current: string | null } = { current: null };
  const running: { [key: string]: boolean } = {};
  return async (
    id: string | null,
    fetcher: (cursor?: string | null) => FetcherResult<T>,
    onFetch: (data: T[], next: string | null) => void,
    onError?: (error: string) => void,
  ) => {
    state.current = id;
    if (id === null) {
      return;
    }
    if (running[id]) {
      return;
    }
    running[id] = true;
    let cursor: string | null = null;
    while (true) {
      const {
        data,
        next,
        error,
      }: { data: T[]; next: string | null; error?: string } = await fetcher(
        cursor,
      );
      if (state.current === null) {
        running[id] = false;
        return;
      }
      cursor = next;
      if (error) {
        onError?.(error);
        running[id] = false;
        return;
      }
      onFetch(data, next);
      if (state.current !== id || next === null) {
        running[id] = false;
        return;
      }
    }
  };
};

const processMessengerAdErrors = (
  messengerAd: MessengerAdModel,
): string | null => {
  const { ads } = messengerAd;
  if (
    messengerAd.syncError === null &&
    ads.find(ad => ad.syncError !== null) === undefined
  ) {
    return null;
  }
  return messengerAd.syncError || 'Some of the ads you tried to sync failed';
};

const isRunningSync = async (
  dispatch: Dispatch<StoreState>,
  botId: number,
  adId: number,
): Promise<[boolean, string | null, MessengerAdModel]> => {
  const messengerAd = await dispatch(
    MessengerAdCRUDActions.retrieveById(botId, adId, false),
  );
  if (messengerAd && messengerAd.inProgress) {
    return [true, null, messengerAd];
  }
  return [false, processMessengerAdErrors(messengerAd), messengerAd];
};

const isStillPublishing = async (
  dispatch: Dispatch<StoreState>,
  botId: number,
  adConfigId: number,
): Promise<[boolean, string | null, AdConfigModel]> => {
  const adConfig = await dispatch(
    AdConfigCRUDActions.retrieveById(botId, adConfigId, false),
  );
  if (adConfig && adConfig.publishingProcess) {
    return [true, null, adConfig];
  }
  return [false, adConfig.publishingError, adConfig];
};

const isStillPublishingSponsored = async (
  dispatch: Dispatch<StoreState>,
  botId: number,
  sponsoredMessageId: number,
): Promise<[boolean, string | null, SponsoredMessageModel]> => {
  const sponsoredMessage = await dispatch(
    sponsoredMessageCRUDActions.retrieveById(botId, sponsoredMessageId, false),
  );
  if (sponsoredMessage && sponsoredMessage.publishingProcess) {
    return [true, null, sponsoredMessage];
  }
  return [false, sponsoredMessage.publishingError, sponsoredMessage];
};

const pollTillReady = async (
  stillPublishing: () => Promise<[boolean, string | null, any]>,
) => {
  const MAX_WAIT_TIME = 2 * 60 * 1000; // 2 minutes
  const POLL_INTERVAL = 3000;
  const pollMaxAttempts = MAX_WAIT_TIME / POLL_INTERVAL;
  let pollAttempts = 0;

  while (pollAttempts < pollMaxAttempts) {
    await timeout(POLL_INTERVAL);
    const [isStillPublishing, error, entity] = await stillPublishing();
    if (!isStillPublishing) return [error, entity] as const;
    pollAttempts += 1;
  }
};

type History = ReturnType<typeof useRouter>['history'];

export const onAccountsScrollEnd = (nextAccounts: string) => () => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  if (nextAccounts) {
    dispatch(
      AdAccountCRUDActions.retrieveAll(facebookPageId, true, nextAccounts),
    );
  }
};

export const onClickSave = async (
  dispatch: Dispatch<StoreState>,
  history: History,
  adConfigId: number,
  chatbotId: number,
  updates: Partial<AdConfigModel>,
  armLandmine: boolean,
  pro: boolean,
  usedInSlideout?: boolean,
  onSuccess?: () => void,
) => {
  if (armLandmine && !pro) {
    return dispatch(
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['CLICK-TO-MESSENGER__ACTIVATE'],
        planFallback: PageBillingType.ProUnicorn,
        stayOnPage: true,
      }),
    );
  }
  const entityOrError = await dispatch(
    AdConfigCRUDActions.update(
      chatbotId,
      { id: adConfigId, ...updates },
      false,
    ),
  );
  if (isArray(entityOrError)) {
    dispatch(
      modalActions.openPrettyErrorModal({
        errorMessage: NETWORK_ERROR_MODAL_TEXT,
        title: NETWORK_ERROR_MODAL_TITLE,
        detailsForNerds: entityOrError.join('; '),
      }),
    );
    return;
  }
  const [error, adConfig] =
    (await pollTillReady(() =>
      isStillPublishing(dispatch, chatbotId, adConfigId),
    )) || [];
  dispatch(AdConfigCRUDActions.unbusy());
  if (error) {
    processError(error, dispatch);
  } else {
    const route = BotBuilderRoutes.campaigns.getLink({
      chatbotId,
      kind: 'ctm_ads',
    });
    if (!usedInSlideout) {
      history.push(route);
    }
    if (onSuccess) {
      onSuccess();
    }
  }
};

export const onClickActivate = async (
  dispatch: Dispatch<StoreState>,
  history: History,
  adConfig: AdConfigModel | null,
  botId: number,
  armLandmine: boolean,
  pro: boolean,
  usedInSlideout?: boolean,
  onSuccess?: () => void,
) => {
  if (armLandmine) {
    if (pro) {
      return dispatch(
        modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames['CTM_ADS_PRO_ACTIVATE'],
          planFallback: PageBillingType.ProUnicorn,
          stayOnPage: true,
        }),
      );
    }
    return dispatch(
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['CLICK-TO-MESSENGER__ACTIVATE'],
        planFallback: PageBillingType.ProUnicorn,
        stayOnPage: true,
      }),
    );
  }
  try {
    await (adConfig &&
      dispatch(AdConfigCRUDActions.publish(botId, adConfig.id)));
  } catch {
    dispatch(AdConfigCRUDActions.unbusy());
    return;
  }
  if (adConfig) {
    const [error, entity] =
      (await pollTillReady(() =>
        isStillPublishing(dispatch, botId, adConfig.id),
      )) || [];
    dispatch(AdConfigCRUDActions.unbusy());
    if (error) {
      processError(error, dispatch);
    } else {
      const route = BotBuilderRoutes.campaigns.getLink({
        chatbotId: botId,
        kind: 'ctm_ads',
      });
      dispatch({ type: ConnectedPageActions.adPublished, key: 'hasCtmAds' });
      if (!usedInSlideout) {
        history.push(route);
      }
      if (onSuccess) {
        onSuccess();
      }
    }
  }
};

const audienceSyncRegexp = /Failed creating custom audience/;

const processError = (error: string, dispatch: Dispatch<StoreState>) => {
  if (audienceSyncRegexp.exec(error)) {
    dispatch(modalActions.openAudiencesSyncRestrictionsModal(error));
  } else {
    renderErrorModal(dispatch, error);
  }
};

const renderErrorModal = (dispatch: Dispatch<StoreState>, error: string) => {
  dispatch(
    modalActions.openPrettyErrorModal({
      errorMessage: NETWORK_ERROR_MODAL_TEXT,
      title: NETWORK_ERROR_MODAL_TITLE,
      detailsForNerds: error,
    }),
  );
};

export const onPublish = (
  dispatch: Dispatch<StoreState>,
  id: number,
  botId: number,
  changes: Partial<SponsoredMessageModel>,
) => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      await dispatch(sponsoredMessageCRUDActions.publish(botId, id, changes));
    } catch {
      const errorMessage = 'Something went wrong, please try again';
      dispatch(sponsoredMessageCRUDActions.unbusy());
      renderErrorModal(dispatch, errorMessage);
      reject(errorMessage);
      return;
    }
    const [error, entity] =
      (await pollTillReady(() =>
        isStillPublishingSponsored(dispatch, botId, id),
      )) || [];
    dispatch(sponsoredMessageCRUDActions.unbusy());
    if (error) {
      processError(error, dispatch);
      reject(error);
      return;
    }
    resolve(entity);
  });
};

export const onClickSync = async (
  dispatch: Dispatch<StoreState>,
  history: History,
  messengerAd: MessengerAdModel | null,
  body: { ads?: MessengerAdRemoteAdModel[] },
  botId: number,
  armLandmine: boolean,
  pro: boolean,
  onFinished: (ad: MessengerAdModel) => void,
  usedInSlideout?: boolean,
  onSuccess?: () => void,
) => {
  if (armLandmine) {
    if (pro) {
      if ((messengerAd?.ads?.length || 0) > 1) {
        return dispatch(
          modalActions.openUniversalUpgradeLandmineModal({
            landmineName: landmineNames['CTM_ADS_PRO_SYNC_MULTIPLE'],
            planFallback: PageBillingType.ProUnicorn,
            stayOnPage: true,
          }),
        );
      }
      if (messengerAd?.draft) {
        return dispatch(
          modalActions.openUniversalUpgradeLandmineModal({
            landmineName: landmineNames['CTM_ADS_PRO_SYNC'],
            planFallback: PageBillingType.ProUnicorn,
            stayOnPage: true,
          }),
        );
      }
    } else {
      return dispatch(
        modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames['MESSAGING-ADS__AD-DETAILS-SYNC'],
          planFallback: PageBillingType.ProUnicorn,
          stayOnPage: true,
        }),
      );
    }
  }
  try {
    await (messengerAd &&
      dispatch(MessengerAdCRUDActions.sync(botId, messengerAd.id, body)));
  } catch {
    dispatch(MessengerAdCRUDActions.unbusy());
    return;
  }
  if (messengerAd) {
    const [error, entity] =
      (await pollTillReady(() =>
        isRunningSync(dispatch, botId, messengerAd.id),
      )) || [];
    dispatch(MessengerAdCRUDActions.unbusy());
    onFinished(entity);
    if (error) {
      processError(error, dispatch);
    } else {
      const route = BotBuilderRoutes.campaigns.getLink({
        chatbotId: botId,
        kind: 'ctm_ads',
      });
      dispatch({ type: ConnectedPageActions.adPublished, key: 'hasCtmAds' });
      if (!usedInSlideout) {
        history.push(route);
      }
      if (onSuccess) {
        onSuccess();
      }
    }
  }
};
